import { useRef, useCallback, useState, useEffect } from 'react';
import { Output, BufferTarget, Mp4OutputFormat, CanvasSource, VideoEncodingConfig } from 'mediabunny';
import type { WebGLRenderer } from 'three';

interface VideoRecorderOptions {
  width: number;
  height: number;
  fps?: number;
  bitrate?: number;
}

interface VideoRecorderState {
  isRecording: boolean;
  isProcessing: boolean;
  progress: number;
}

export const useVideoRecorder = (
  renderer: WebGLRenderer | null,
  options: VideoRecorderOptions
) => {
  const [state, setState] = useState<VideoRecorderState>({
    isRecording: false,
    isProcessing: false,
    progress: 0,
  });

  const outputRef = useRef<Output<Mp4OutputFormat, BufferTarget> | null>(null);
  const canvasSourceRef = useRef<CanvasSource | null>(null);
  const targetRef = useRef<BufferTarget | null>(null);
  const recordedBlobRef = useRef<Blob | null>(null); // Store the blob after recording stops
  const frameCountRef = useRef<number>(0);
  const intervalRef = useRef<number | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const needsOffscreenCanvasRef = useRef<boolean>(false);
  const evenWidthRef = useRef<number>(0);
  const evenHeightRef = useRef<number>(0);

  const { width, height, fps = 30, bitrate = 10_000_000 } = options;

  const startRecording = useCallback(async () => {
    if (!renderer) {
      console.error('Cannot start recording: renderer is null');
      return;
    }
    
    if (isRecordingRef.current) {
      return;
    }

    try {
      // Get actual canvas dimensions from renderer
      const actualCanvas = renderer.domElement;
      const actualWidth = actualCanvas.width;
      const actualHeight = actualCanvas.height;

      // Use actual canvas dimensions instead of passed dimensions
      // Round dimensions to nearest even numbers (required for AVC codec)
      const evenWidth = Math.floor(actualWidth / 2) * 2;
      const evenHeight = Math.floor(actualHeight / 2) * 2;
      
      // Store in refs for use in captureFrame closure
      evenWidthRef.current = evenWidth;
      evenHeightRef.current = evenHeight;
      
      // Always use offscreen canvas to ensure CanvasSource sees even dimensions
      // CanvasSource reads dimensions from the canvas, so we need to ensure it's always even
      const needsOffscreenCanvas = actualWidth !== evenWidth || actualHeight !== evenHeight;
      needsOffscreenCanvasRef.current = needsOffscreenCanvas;
      
      // Always create offscreen canvas with even dimensions
      // This ensures CanvasSource always sees even dimensions
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = evenWidth;
      offscreenCanvas.height = evenHeight;
      const offscreenCtx = offscreenCanvas.getContext('2d', {
        willReadFrequently: false,
        alpha: false,
      });
      if (!offscreenCtx) {
        throw new Error('Failed to get 2D context for offscreen canvas');
      }
      // Disable image smoothing for pixel-perfect copying
      offscreenCtx.imageSmoothingEnabled = false;
      offscreenCanvasRef.current = offscreenCanvas;
      offscreenCtxRef.current = offscreenCtx;
      
      const recordingCanvas = offscreenCanvas;

      // Create buffer target for in-memory storage
      const target = new BufferTarget();
      targetRef.current = target;

      // Create MP4 output format
      const format = new Mp4OutputFormat({
        fastStart: 'in-memory',
      });

      // Create output
      const output = new Output({
        format,
        target,
      });

      // Create video encoding config
      const encodingConfig: VideoEncodingConfig = {
        codec: 'avc',
        bitrate,
      };

      // Create canvas source using the recording canvas (original or offscreen)
      const canvasSource = new CanvasSource(recordingCanvas, encodingConfig);
      canvasSourceRef.current = canvasSource;

      // Add video track
      output.addVideoTrack(canvasSource, {
        frameRate: fps,
      });

      // Start output
      await output.start();

      outputRef.current = output;

      // Reset counters
      frameCountRef.current = 0;
      isRecordingRef.current = true;

      setState({
        isRecording: true,
        isProcessing: false,
        progress: 0,
      });

      // Capture frames at specified FPS
      const frameInterval = 1000 / fps;
      let lastFrameTime = performance.now();

      const captureFrame = () => {
        if (!isRecordingRef.current || !canvasSourceRef.current || !renderer) {
          return;
        }

        const now = performance.now();
        const elapsed = now - lastFrameTime;

        if (elapsed >= frameInterval) {
          // Always copy frame from original canvas to offscreen canvas
          // This ensures CanvasSource always sees even dimensions
          if (offscreenCtxRef.current) {
            const originalCanvas = renderer.domElement;
            const evenWidth = evenWidthRef.current;
            const evenHeight = evenHeightRef.current;
            
            // Get actual canvas dimensions (may differ from passed dimensions due to DPR)
            const actualCanvasWidth = originalCanvas.width;
            const actualCanvasHeight = originalCanvas.height;
            
            // Crop from actual canvas to even dimensions (no scaling to preserve quality)
            offscreenCtxRef.current.clearRect(0, 0, evenWidth, evenHeight);
            offscreenCtxRef.current.drawImage(
              originalCanvas,
              0, 0, Math.min(evenWidth, actualCanvasWidth), Math.min(evenHeight, actualCanvasHeight),  // Source: crop from actual canvas
              0, 0, evenWidth, evenHeight   // Destination: same size (no scaling)
            );
          }

          const timestamp = frameCountRef.current / fps; // seconds
          const duration = 1 / fps; // seconds

          // Add frame to canvas source (don't await to avoid blocking)
          canvasSourceRef.current.add(timestamp, duration).catch((error) => {
            console.error('Error capturing frame:', error);
          });
          
          frameCountRef.current++;
          lastFrameTime = now;
        }

        // Continue capturing if still recording
        if (isRecordingRef.current) {
          intervalRef.current = requestAnimationFrame(captureFrame);
        }
      };

      // Start capturing
      intervalRef.current = requestAnimationFrame(captureFrame);
    } catch (error) {
      console.error('Error starting recording:', error);
      isRecordingRef.current = false;
      setState({
        isRecording: false,
        isProcessing: false,
        progress: 0,
      });
    }
  }, [renderer, width, height, fps, bitrate]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!isRecordingRef.current || !outputRef.current || !canvasSourceRef.current) {
      return null;
    }

    isRecordingRef.current = false;

    // Cancel animation frame
    if (intervalRef.current !== null) {
      cancelAnimationFrame(intervalRef.current);
      intervalRef.current = null;
    }

    setState({
      isRecording: false,
      isProcessing: true,
      progress: 0,
    });

    try {
      // Wait a bit to ensure last frame is captured
      // This gives time for any pending frame captures to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Close canvas source - this signals no more frames will be added
      canvasSourceRef.current.close();

      // Wait a bit more for encoder to process remaining frames
      await new Promise(resolve => setTimeout(resolve, 200));

      // Finalize output
      await outputRef.current.finalize();

      // Get the buffer
      const buffer = targetRef.current!.buffer;

      if (!buffer) {
        console.error('Buffer is null after finalization');
        throw new Error('Buffer is null after finalization');
      }

      if (buffer.byteLength === 0) {
        console.error('Buffer is empty, frame count was:', frameCountRef.current);
        throw new Error(`Recording is empty. No frames were captured (frame count: ${frameCountRef.current})`);
      }

      // Create blob and store it BEFORE cleanup
      const blob = new Blob([buffer], { type: 'video/mp4' });
      recordedBlobRef.current = blob;

      // Cleanup refs but keep the blob
      canvasSourceRef.current = null;
      outputRef.current = null;
      targetRef.current = null;
      offscreenCanvasRef.current = null;
      offscreenCtxRef.current = null;

      setState({
        isRecording: false,
        isProcessing: false,
        progress: 100,
      });

      return blob;
    } catch (error) {
      console.error('Error finalizing recording:', error);
      setState({
        isRecording: false,
        isProcessing: false,
        progress: 0,
      });
      throw error;
    }
  }, []);

  const downloadVideo = useCallback(async (filename: string = 'recording.mp4') => {
    // Get blob - either from stopRecording or from stored blob
    let blob: Blob | null = null;
    
    if (isRecordingRef.current || outputRef.current) {
      // Still recording, need to stop first
      blob = await stopRecording();
    } else if (recordedBlobRef.current) {
      // Use stored blob from previous recording
      blob = recordedBlobRef.current;
    } else {
      console.error('No video blob available for download');
      throw new Error('No video data available. Please record something first.');
    }
    
    if (!blob || blob.size === 0) {
      console.error('Invalid blob:', blob);
      throw new Error('Video file is empty or invalid. Please try recording again.');
    }

    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      
      // Trigger download
      a.click();
      
      // Wait a bit before removing to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error triggering download:', error);
      throw error;
    }
  }, [stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        cancelAnimationFrame(intervalRef.current);
      }
      if (canvasSourceRef.current) {
        canvasSourceRef.current.close();
      }
      if (outputRef.current) {
        outputRef.current.cancel().catch(() => {
          // Ignore cancellation errors
        });
      }
    };
  }, []);

  // Return the recorder object - don't memoize to ensure state updates propagate
  return {
    ...state,
    startRecording,
    stopRecording,
    downloadVideo,
  };
};

